<template>
  <div>
    <loading-spinner v-if="!chain" />
    <app-header v-if="chain" :title="originalName ? editTitle : newTitle">
      <template #col-right>
        <b-button
          :disabled="buttonDisabled"
          variant="primary"
          class="mr-1"
          type="submit"
          @click="save"
        >
          <span v-label-key="'SAVE_CHANGES'">Save Changes</span>
        </b-button>
        <cancel-button />
      </template>
    </app-header>

    <div v-if="chain && distributors && subdistributors" class="container">
      <b-row>
        <b-col md="6">
          <errors v-if="errors.length" :errors="errors" />
          <b-form ref="editorForm">
            <h3 v-label-key="'EDIT_CORE_INFORMATION_TITLE'">
              Core Information
            </h3>
            <b-form-group
              :label="`${getLabel('DISTRIBUTOR')} *`"
              :description="getLabel('EDIT_CHAIN_DISTRIBUTOR_DESCRIPTION')"
            >
              <b-form-select
                v-model="chain.distributor_id"
                class="form-control"
                convert-to-number
                ng-disabled="auth.user.selectedAccess.roleLevel < 4"
                required
              >
                <b-form-select-option
                  v-for="distributor in distributors"
                  :key="distributor.id"
                  :value="distributor.id"
                >
                  {{ distributor.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="getLabel('SUBDISTRIBUTOR')"
              :description="getLabel('EDIT_CHAIN_SUBDISTRIBUTOR_DESCRIPTION')"
            >
              <b-form-select
                v-model="chain.subdistributor_id"
                class="form-control"
                :disabled="auth.user.selectedAccess.roleLevel < 3"
              >
                <b-form-select-option :value="null">
                  <span v-label-key="'NONE'">None</span>
                </b-form-select-option>
                <b-form-select-option
                  v-for="subdistributor in displaySubdistributors"
                  :key="subdistributor.id"
                  :value="subdistributor.id"
                >
                  {{ subdistributor.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>

            <b-form-group
              :label="`${getLabel('NAME')} *`"
              :description="getLabel('EDIT_CHAIN_NAME_DESCRIPTION')"
            >
              <b-form-input v-model="chain.name" type="text" required />
            </b-form-group>

            <br />
            <h3 v-label-key="'EDIT_CHAIN_RETAIL_APP_TITLE'">
              Retail App Options:
            </h3>
            <p v-label-key="'EDIT_CHAIN_RETAIL_APP_DESCRIPTION'">
              Important! All the following options affect the Retail app for
              stores belonging to this chain
            </p>
            <div
              v-for="(section, $index) in toggleSections"
              :key="`togglesection-${$index}`"
            >
              <br />
              <h4 v-label-key="section.titleKey">{{ section.title }}</h4>
              <p v-label-key="section.descriptionKey">
                {{ section.description }}
              </p>
              <label
                v-for="toggle of section.toggles"
                :key="toggle.id"
                class="form-check"
              >
                <input
                  v-model="chain[toggle.id]"
                  type="checkbox"
                  class="form-check-input"
                />
                <span class="form-check-label" v-label-key="toggle.titleKey">{{
                  toggle.title
                }}</span>
                <small
                  v-if="toggle.description"
                  class="form-text text-muted"
                  v-label-key="toggle.descriptionKey"
                  >{{ toggle.description }}</small
                >
              </label>
            </div>
            <div>
              <br />
              <h4 v-label-key="'CHAIN_SETTING_ENABLE_STAFF_ID_OPTIONS'">
                Staff ID Options
              </h4>
              <p
                v-label-key="
                  'CHAIN_SETTING_ENABLE_STAFF_ID_OPTIONS_DESCRIPTIONS'
                "
              >
                Different options for requiring or recording the Retail user's
                Staff ID
              </p>
              <label class="form-check">
                <input
                  v-model="chain.enable_staffid"
                  type="checkbox"
                  class="form-check-input"
                  @input="toggleStaffID"
                />
                <span
                  class="form-check-label"
                  v-label-key="'CHAIN_SETTING_ENABLE_STAFF_ID'"
                  >Enable Staff ID</span
                >
                <small
                  class="form-text text-muted"
                  v-label-key="'CHAIN_SETTING_ENABLE_STAFF_ID_DESCRIPTION'"
                  >Enable the Staff ID feature</small
                >
              </label>
              <div class="form-indent">
                <label class="form-check">
                  <input
                    v-model="chain.enable_staffid_scan"
                    type="checkbox"
                    class="form-check-input"
                    @input="toggleStaffIdSub('enable_staffid_scan', $event)"
                  />
                  <span
                    class="form-check-label"
                    v-label-key="'CHAIN_SETTING_ENABLE_STAFF_ID_SCAN'"
                    >Enable Staff ID Scan</span
                  >
                  <small
                    class="form-text text-muted"
                    v-label-key="
                      'CHAIN_SETTING_ENABLE_STAFF_ID_SCAN_DESCRIPTION'
                    "
                    >Option to scan Staff ID QR code</small
                  >
                </label>
                <label class="form-check">
                  <input
                    v-model="chain.enable_staffid_type"
                    type="checkbox"
                    class="form-check-input"
                    @input="toggleStaffIdSub('enable_staffid_type', $event)"
                  />
                  <span
                    class="form-check-label"
                    v-label-key="'CHAIN_SETTING_ENABLE_STAFF_ID_TYPE'"
                    >Enable Staff ID Type</span
                  >
                  <small
                    class="form-text text-muted"
                    v-label-key="
                      'CHAIN_SETTING_ENABLE_STAFF_ID_TYPE_DESCRIPTION'
                    "
                    >Option to type in the Staff ID using app keyboard</small
                  >
                </label>
                <label class="form-check">
                  <input
                    v-model="chain.enable_staffid_skip"
                    type="checkbox"
                    class="form-check-input"
                    @input="toggleStaffIdSub('enable_staffid_skip', $event)"
                  />
                  <span
                    class="form-check-label"
                    v-label-key="'CHAIN_SETTING_ENABLE_STAFF_ID_SKIP'"
                    >Enable Staff ID Skip</span
                  >
                  <small
                    class="form-text text-muted"
                    v-label-key="
                      'CHAIN_SETTING_ENABLE_STAFF_ID_SKIP_DESCRIPTION'
                    "
                    >Option to allow skipping the Staff ID requirement</small
                  >
                </label>
              </div>
            </div>
            <br />
            <h4 v-label-key="'EDIT_CHAIN_GENERAL_TITLE'">General</h4>
            <p v-label-key="'EDIT_CHAIN_GENERAL_DESCRIPTION'">
              General settings for the Retail App
            </p>

            <b-form-group
              :label="getLabel('SCREENSAVER')"
              :description="getLabel('SCREENSAVER_DESCRIPTION')"
            >
              <b-form-select
                v-model="chain.screensaver_id"
                class="form-control"
                :disabled="!screensaverOptions"
              >
                <b-form-select-option value="">
                  <span v-label-key="'NONE'">None</span>
                </b-form-select-option>
                <b-form-select-option
                  v-for="screensaver in screensaverOptions"
                  :key="screensaver.id"
                  :value="screensaver.id"
                >
                  {{ screensaver.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-form>
        </b-col>
        <b-col md="8" />
      </b-row>
    </div>
  </div>
</template>

<script>
import $api from '@/services/api';
import $auth from '@/services/auth';
import CancelButton from '@/components/CancelButton';
import LoadingSpinner from '@/components/LoadingSpinner';
import Errors from '@/components/Errors';
import AppHeader from '@/components/AppHeader';
import { mapGetters } from 'vuex';

export default {
  components: {
    CancelButton,
    LoadingSpinner,
    Errors,
    AppHeader,
  },
  data() {
    return {
      auth: $auth,
      originalName: null,
      chain: null,
      subdistributors: null,
      distributors: null,
      errors: [],
      buttonDisabled: false,
      screensaverOptions: null,
      toggleSections: [
        // Retail app Home page options
        {
          title: 'Home Page',
          titleKey: 'CHAIN_SETTING_HOME_PAGE',
          description: 'Options displayed on the Retail app Home Page',
          descriptionKey: 'CHAIN_SETTING_HOME_PAGE_DESCRIPTION',
          toggles: [
            {
              id: 'enable_blank_cut',
              title: 'Enable Blank Cut',
              titleKey: 'CHAIN_SETTING_ENABLE_BLANK_CUT',
              description:
                'Enables cutting functionality on the Retail App for stores in the chain',
              descriptionKey: 'CHAIN_SETTING_ENABLE_BLANK_CUT_DESCRIPTION',
            },
            {
              id: 'enable_customer_order',
              title: 'Enable Customer Order',
              titleKey: 'CHAIN_SETTING_ENABLE_CUSTOMER_ORDER',
              description:
                "Enable customer order function for the chain. This is for Gokaizen app. Scan Order ID or type Order ID to load the device's template",
              descriptionKey: 'CHAIN_SETTING_ENABLE_CUSTOMER_ORDER_DESCRIPTION',
            },
            {
              id: 'enable_design',
              title: 'Enable Design',
              titleKey: 'CHAIN_SETTING_ENABLE_DESIGN',
              description: 'Enable a Design button which link to Gokaizen App',
              descriptionKey: 'CHAIN_SETTING_ENABLE_DESIGN_DESCRIPTION',
            },
            {
              id: 'enable_fourseasons',
              title: 'Has Four Seasons Printer',
              titleKey: 'STORE_SETTING_ENABLE_HAS_FOUR_SEASONS_PRINTER',
              description:
                "Enable Four Seasons printing service for that chain's store",
              descriptionKey:
                'STORE_SETTING_ENABLE_HAS_FOUR_SEASONS_PRINTER_DESC',
            },
          ],
        },

        // Retail app Menu options
        {
          title: 'Menu Page',
          titleKey: 'CHAIN_SETTING_MENU_PAGE',
          description: 'Options displayed on the Retail app Menu Page',
          descriptionKey: 'CHAIN_SETTING_MENU_PAGE_DESCRIPTION',
          toggles: [
            {
              id: 'enable_help',
              title: 'Enable Help (recommended)',
              titleKey: 'CHAIN_SETTING_ENABLE_HELP',
              description: 'Enable Help menu for the chain',
              descriptionKey: 'CHAIN_SETTING_ENABLE_HELP_DESCRIPTION',
            },
            {
              id: 'enable_rankings',
              title: 'Enable Rankings (recommended)',
              titleKey: 'CHAIN_SETTING_ENABLE_RANKINGS',
              description:
                "Enable Rankings feature for the chain. It's a brief summary of store & chain activity over the last 30 days",
              descriptionKey: 'CHAIN_SETTING_ENABLE_RANKINGS_DESCRIPTION',
            },
            {
              id: 'enable_stats',
              title: 'Enable Stats (recommended)',
              description:
                'Enable Stats feature for the chain. A quick stats for last 30 days.  Includes: cut by status, date, device type, device, and manufacturer',
              descriptionKey: 'CHAIN_SETTING_ENABLE_STATS_DESCRIPTION',
            },
            {
              id: 'enable_referrals',
              title: 'Enable Referrals',
              titleKey: 'CHAIN_SETTING_ENABLE_REFERRALS',
              description: 'Enable Rewards feature for the chain',
              descriptionKey: 'CHAIN_SETTING_ENABLE_REFERRALS_DESCRIPTION',
            },
            {
              id: 'enable_register_fourseasons',
              title: 'Enable Register Four Seasons',
              titleKey: 'CHAIN_SETTING_ENABLE_REGISTER_FOUR_SEASONS',
              description:
                'Enable Four Seasons pack registration for the chain.  Only for chains that support the Four Seasons feature',
              descriptionKey: 'CHAIN_SETTING_ENABLE_REGISTER_FOUR_SEASONS_DESC',
            },
          ],
        },

        // Retail app Blank Cut options
        {
          title: 'Blank Cut Page',
          titleKey: 'CHAIN_SETTING_BLANK_CUT_PAGE',
          description:
            'Options displayed on the Blank Cut page.  Allows the Retail app user to view a list of devices ordered by the following categories',
          descriptionKey: 'CHAIN_SETTING_BLANK_CUT_PAGE_DESCRIPTION',
          toggles: [
            {
              id: 'enable_list_recent',
              title: 'Enable List Recent',
              titleKey: 'CHAIN_SETTING_ENABLE_LIST_RECENT',
              description: '',
            },
            {
              id: 'enable_list_frequently_used',
              title: 'Enable List Frequently Used',
              titleKey: 'CHAIN_SETTING_ENABLE_LIST_FREQUENTLY_USED',
              description: '',
            },
            {
              id: 'enable_list_new',
              title: 'Enable List New',
              titleKey: 'CHAIN_SETTING_ENABLE_LIST_NEW',
              description: '',
            },
            {
              id: 'enable_list_popular',
              title: 'Enable List Popular',
              titleKey: 'CHAIN_SETTING_ENABLE_LIST_POPULAR',
              description: '',
            },
            {
              id: 'enable_list_search',
              title: 'Enable List Search',
              titleKey: 'CHAIN_SETTING_ENABLE_LIST_SEARCH',
              description: '',
            },
          ],
        },

        // Retail app types of templates to display
        // This is handled in template type tab now
        {
          title: 'Types of templates',
          titleKey: 'CHAIN_SETTING_TYPES_OF_TEMPLATES',
          description: 'Enables different types of templates for stores',
          descriptionKey: 'CHAIN_SETTING_TYPES_OF_TEMPLATES_DESCRIPTION',
          toggles: [
            // {
            //       id: 'enable_templates_air',
            //       title: 'Enable Templates Air',
            //       titleKey: 'CHAIN_SETTING_ENABLE_TEMPLATES_AIR',
            //       description: '',
            //     },
            //     {
            //       id: 'enable_templates_back_full',
            //       title: 'Enable Templates Back Full',
            //       titleKey: 'CHAIN_SETTING_ENABLE_TEMPLATES_BACK_FULL',
            //       description: '',
            //     },
            {
              id: 'enable_templates_easy_install',
              title: 'Enable Templates Easy Install',
              titleKey: 'CHAIN_SETTING_ENABLE_TEMPLATES_EASY_INSTALL',
              description: '',
            },
            //     {
            //       id: 'enable_templates_front_full',
            //       title: 'Enable Templates Front Full',
            //       titleKey: 'CHAIN_SETTING_ENABLE_TEMPLATES_FRONT_FULL',
            //       description: '',
            //     },
          ],
        },

        // Retail app options to scan film code or use a bypass credit
        {
          title: 'Scanning Film QR or Using Credits',
          titleKey: 'CHAIN_SETTING_ENABLE_SCAN_FILM_BYPASS',
          description: '',
          toggles: [
            {
              id: 'enable_scan_film_code',
              title: 'Enable Scan Film Code',
              titleKey: 'CHAIN_SETTING_ENABLE_SCAN_FILM_CODE',
              description:
                'Allows the Retail app user to scan the QR code on a film in order to perform a cut',
              descriptionKey: 'CHAIN_SETTING_ENABLE_SCAN_FILM_CODE_DESC',
            },
            {
              id: 'enable_bypass_credit',
              title: 'Enable Bypass Credit',
              titleKey: 'CHAIN_SETTING_ENABLE_BYPASS_CREDIT',
              description:
                'Allows the Retail app user to use a bypass credit in order to perform a cut',
              descriptionKey: 'CHAIN_SETTING_ENABLE_BYPASS_CREDIT_DESC',
            },
          ],
        },

        // Retail app staff ID options
        // Moved to HTML

        // Enable/disable all stores
        // enable_store: 'Enable Stores',// Not requested at this point
      ],
    };
  },

  computed: {
    ...mapGetters('translations', {
      getLabel: 'getLabel',
      getReplaceLabel: 'getReplaceLabel',
    }),
    editTitle() {
      return this.getReplaceLabel('EDIT_ITEM_NAME', {
        '{NAME}': this.originalName,
      });
    },
    newTitle() {
      return this.getReplaceLabel('NEW_ITEM_NAME', {
        '{NAME}': this.getLabel('CHAIN'),
      });
    },
    displaySubdistributors() {
      return this.subdistributors.filter(
        subdistributor =>
          subdistributor.distributor_id === this.chain.distributor_id
      );
    },
    selectedDistributor() {
      if (!this.chain) return null;
      return this.chain.distributor_id;
    },
  },

  created() {
    this.refreshData();
  },

  methods: {
    refreshData() {
      $api.get('/v1/dashboard/distributors').then(distributors => {
        this.distributors = distributors.data;
      });
      $api.get('/v1/dashboard/subdistributors').then(subdistributors => {
        this.subdistributors = subdistributors.data;
      });

      if (this.$route.query.id) {
        $api.get(`/v1/dashboard/chains/${this.$route.query.id}`).then(chain => {
          this.originalName = chain.name;
          this.chain = chain;
        });
      } else {
        this.chain = {
          distributor_id:
            parseInt(this.$route.query.distributor_id, 10) ||
            $auth.user.selectedAccess.distributor_id,
          subdistributor_id:
            parseInt(this.$route.query.subdistributor_id, 10) ||
            $auth.user.selectedAccess.subdistributor_id,
          enable_blank_cut: true,
          enable_list_recent: true,
          enable_list_frequently_used: true,
          enable_list_new: true,
          enable_list_popular: true,
          enable_list_search: true,
          enable_scan_film_code: true,
          enable_bypass_credit: true,
        };
      }
    },
    validateForm() {
      this.errors = [];

      if (!this.chain.distributor_id) {
        this.errors.push(
          this.getLabel('FORM_ERROR_DISTRIBUTOR_REQUIRED') ||
            'The distributor field is required.'
        );
      }

      if (!this.chain.name) {
        this.errors.push(
          this.getLabel('FORM_ERROR_NAME_REQUIRED') ||
            'The name field is required.'
        );
      }
    },
    save() {
      this.buttonDisabled = true;
      if (!this.$refs.editorForm.checkValidity()) {
        this.$refs.editorForm.reportValidity();
        this.validateForm();
        return;
      }

      this.errors = [];

      if (this.chain.id) {
        $api
          .put(`/v1/dashboard/chains/${this.chain.id}`, this.chain)
          .then(chain => {
            this.$router.push({
              name: 'chain-detail',
              params: chain,
            });
            this.buttonDisabled = false;
          });
      } else {
        $api.post('/v1/dashboard/chains', this.chain).then(chain => {
          this.$router.push({
            name: 'chain-detail',
            params: chain,
          });
          this.buttonDisabled = false;
        });
      }
    },
    toggleStaffID($event) {
      const { checked } = $event.target;
      this.$set(this.chain, 'enable_staffid_scan', checked);
      this.$set(this.chain, 'enable_staffid_type', checked);
      this.$set(this.chain, 'enable_staffid_skip', checked);
    },
    async toggleStaffIdSub(toggleId, $event) {
      const { checked } = $event.target;
      if (checked) {
        this.chain.enable_staffid = true;
        return;
      }

      const props = [
        'enable_staffid_scan',
        'enable_staffid_type',
        'enable_staffid_skip',
      ];
      const enabled = props.filter(prop => {
        return prop !== toggleId && this.chain[prop];
      });
      this.$set(this.chain, 'enable_staffid', enabled.length > 0);
    },
  },
  watch: {
    selectedDistributor() {
      this.screensaverOptions = null;
      if (this.selectedDistributor) {
        $api
          .get(
            `/v1/dashboard/screensavers/distributors/${this.selectedDistributor}`
          )
          .then(screensavers => {
            this.screensaverOptions = screensavers;
          });
      }
    },
  },
};
</script>
<style scoped>
.form-indent {
  margin-left: 20px;
}
</style>
